import React from 'react';
import { Lock, ChevronUp, ChevronDown, Check, Copy } from 'lucide-react';
import type { ApiDocEndpoint } from '../../../../shared/types/index.js';

interface EndpointItemProps {
  endpoint: ApiDocEndpoint;
  isExpanded: boolean;
  token?: string | null;
  copiedIndex: string | null;
  onToggle: () => void;
  onCopy: (text: string, id: string) => void;
}

const methodColors: Record<string, { bg: string; text: string; border: string }> = {
  GET: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  POST: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  PUT: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  PATCH: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  DELETE: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
};

export const EndpointItem: React.FC<EndpointItemProps> = ({
  endpoint: ep,
  isExpanded,
  token,
  copiedIndex,
  onToggle,
  onCopy,
}) => {
  const epKey = `${ep.method}-${ep.path}`;
  const methodStyle = methodColors[ep.method] || methodColors.GET;

  const curlSample = `curl -X ${ep.method} "${window.location.origin}${ep.path}" \\
  -H "Authorization: Bearer ${token ? `${token.substring(0, 16)}...` : '<TOKEN>'}" \\
  -H "Content-Type: application/json"${ep.requestBody ? ` \\\n  -d '${JSON.stringify(ep.requestBody)}'` : ''}`;

  return (
    <div className="p-4 transition hover:bg-slate-50/30">
      <div
        onClick={onToggle}
        className="flex items-center justify-between cursor-pointer gap-3"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`px-2.5 py-1 rounded-md text-xs font-mono font-bold border ${methodStyle.bg} ${methodStyle.text} ${methodStyle.border}`}>
            {ep.method}
          </span>
          <span className="font-mono text-xs font-semibold text-slate-900 truncate">
            {ep.path}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {ep.authRequired ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
              <Lock className="w-3 h-3" />
              <span>Perlu Auth</span>
            </span>
          ) : (
            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              Publik
            </span>
          )}

          <button className="text-slate-400 hover:text-slate-600 p-1">
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-1.5 ml-1">
        {ep.description}
      </p>

      {/* Collapsible Details */}
      {isExpanded && (
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-3 animate-in fade-in duration-150">
          {/* Allowed Roles */}
          {ep.roles && (
            <div className="text-xs flex items-center gap-2">
              <span className="text-slate-500 font-medium">Izin Peran:</span>
              <div className="flex gap-1">
                {(ep.roles || []).map((r: string) => (
                  <span key={r} className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-medium text-[11px]">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Request Body */}
          {ep.requestBody && (
            <div>
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">
                Skema Parameter Permintaan (JSON Body)
              </span>
              <pre className="p-3 bg-slate-50 rounded-xl font-mono text-[11px] text-slate-800 border border-slate-200 overflow-x-auto">
                {JSON.stringify(ep.requestBody, null, 2)}
              </pre>
            </div>
          )}

          {/* Query Params */}
          {ep.queryParams && (
            <div>
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">
                Query Parameters
              </span>
              <pre className="p-3 bg-slate-50 rounded-xl font-mono text-[11px] text-slate-800 border border-slate-200 overflow-x-auto">
                {JSON.stringify(ep.queryParams, null, 2)}
              </pre>
            </div>
          )}

          {/* cURL Example */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                Contoh Eksekusi cURL
              </span>
              <button
                onClick={() => onCopy(curlSample, `curl-${epKey}`)}
                className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 cursor-pointer"
              >
                {copiedIndex === `curl-${epKey}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                <span>{copiedIndex === `curl-${epKey}` ? 'Tersalin' : 'Salin cURL'}</span>
              </button>
            </div>
            <pre className="p-3 bg-slate-900 text-emerald-400 rounded-xl font-mono text-[11px] overflow-x-auto">
              {curlSample}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
